import { useState } from 'react';
import { Box, chakra, Flex, Heading, Input, Text, Textarea } from '@chakra-ui/react';
import { executeAction } from '@/lattice/runtime';
import { usePortalUser } from '@/auth/roles';

export const inputStyles = {
  px: '0.9rem',
  py: '0.6rem',
  bg: 'paper.white',
  border: '1px solid',
  borderColor: 'paper.border',
  fontFamily: 'body',
  fontSize: '0.85rem',
  color: 'ink.body',
  borderRadius: '8px',
  w: '100%',
  _focus: { borderColor: 'brand.mid', outline: 'none', boxShadow: '0 0 0 1px #3D52A8' },
} as const;

/**
 * Portal text field with the "Enhance with AI" flow. The affordance is a
 * small ✨ icon (kept out of the way):
 *   single-line inputs   — inside the input, right edge
 *   labeled multiline    — to the right of the label
 *   unlabeled multiline  — inside the textarea, top-right corner
 * The enhanced text opens in a modal where it can be edited; only Apply
 * writes it back to the field. (Card-wide enhancement uses the pill-style
 * button in a card's top-right corner instead.)
 */
export function AiField({
  label,
  value,
  onChange,
  context,
  multiline = false,
  placeholder,
  baseline,
}: {
  label?: string;
  value: string;
  onChange: (next: string) => void;
  /** What this text is, e.g. "a service card description" — steers the model. */
  context?: string;
  multiline?: boolean;
  placeholder?: string;
  /** Published value to diff against — when it differs, the field is highlighted green. */
  baseline?: string;
}) {
  const user = usePortalUser();
  const [enhancing, setEnhancing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enhance = async () => {
    if (!value.trim() || enhancing) return;
    setEnhancing(true);
    setError(null);
    try {
      const result = await executeAction<{ enhanced: string }>(
        'ai-assist:enhanceText',
        { text: value, context },
        user
      );
      setDraft(result.enhanced);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enhancement failed');
    } finally {
      setEnhancing(false);
    }
  };

  const icon = (inset?: 'center' | 'top') => (
    <EnhanceIcon
      onClick={() => void enhance()}
      busy={enhancing}
      disabled={!value.trim()}
      inset={inset}
    />
  );

  const changed = baseline !== undefined && value !== baseline;
  const changedStyles = changed
    ? { borderColor: '#8FBF3F', boxShadow: '0 0 0 1px #B5E061' }
    : {};

  return (
    <Box w="100%">
      {label && (
        <Flex align="center" gap="0.45rem" mb="0.35rem">
          <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink">
            {label}
          </Text>
          {multiline && icon()}
        </Flex>
      )}
      <Box position="relative">
        {multiline ? (
          <Textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            minH="90px"
            resize="vertical"
            {...inputStyles}
            pr={!label ? '2.6rem' : undefined}
            {...changedStyles}
          />
        ) : (
          <Input
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            {...inputStyles}
            pr="2.6rem"
            {...changedStyles}
          />
        )}
        {!multiline && icon('center')}
        {multiline && !label && icon('top')}
      </Box>
      {error && (
        <Text fontSize="0.72rem" color="brand.terracotta" mt="0.3rem">
          {error}
        </Text>
      )}

      {draft !== null && (
        <EnhanceModal
          original={value}
          draft={draft}
          onDraftChange={setDraft}
          onApply={() => {
            onChange(draft);
            setDraft(null);
          }}
          onCancel={() => setDraft(null)}
        />
      )}
    </Box>
  );
}

/**
 * The compact ✨ affordance. inset='center' pins it inside a single-line
 * input's right edge; inset='top' pins it in a textarea's top-right corner.
 */
function EnhanceIcon({
  onClick,
  busy,
  disabled,
  inset,
}: {
  onClick: () => void;
  busy: boolean;
  disabled: boolean;
  inset?: 'center' | 'top';
}) {
  const positioning =
    inset === 'center'
      ? { position: 'absolute' as const, right: '0.4rem', top: '50%', transform: 'translateY(-50%)' }
      : inset === 'top'
        ? { position: 'absolute' as const, right: '0.4rem', top: '0.4rem' }
        : {};

  return (
    <chakra.button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      title="Enhance with AI"
      aria-label="Enhance with AI"
      {...positioning}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      w="24px"
      h="24px"
      flexShrink={0}
      bg="transparent"
      border="none"
      borderRadius="full"
      fontSize="0.8rem"
      lineHeight="1"
      cursor="pointer"
      opacity={disabled ? 0.3 : busy ? 0.6 : 0.75}
      transition="opacity 0.15s, background 0.15s"
      _hover={busy || disabled ? {} : { opacity: 1, bg: 'brand.wash' }}
      animation={busy ? 'btnGreenFlash 1.4s ease-out infinite' : undefined}
    >
      ✨
    </chakra.button>
  );
}

/**
 * Card-wide enhance affordance — the pill button, for a card container's
 * top-right corner (pass as EditorCard header actions).
 */
export function EnhancePill({
  onClick,
  busy,
  disabled = false,
}: {
  onClick: () => void;
  busy: boolean;
  disabled?: boolean;
}) {
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      title="Enhance with AI"
      display="inline-flex"
      alignItems="center"
      gap="0.3rem"
      bg="brand.wash"
      color="brand.mid"
      border="1px solid"
      borderColor="brand.pale"
      fontSize="0.62rem"
      fontWeight="600"
      letterSpacing="0.1em"
      textTransform="uppercase"
      px="0.7rem"
      py="0.35rem"
      borderRadius="14px"
      cursor="pointer"
      opacity={busy || disabled ? 0.5 : 1}
      _hover={busy || disabled ? {} : { bg: 'brand.pale' }}
    >
      {busy ? '…' : '✨ Enhance'}
    </chakra.button>
  );
}

/**
 * The review modal: enhanced text is editable, and nothing touches the form
 * until Apply is clicked. Exported for RichTextField, which drives the same
 * enhance flow from a toolbar button instead of the inline ✨ affordance.
 */
export function EnhanceModal({
  original,
  draft,
  onDraftChange,
  onApply,
  onCancel,
}: {
  original: string;
  draft: string;
  onDraftChange: (next: string) => void;
  onApply: () => void;
  onCancel: () => void;
}) {
  return (
    <Flex
      position="fixed"
      inset="0"
      zIndex="1000"
      bg="rgba(31,45,92,0.45)"
      backdropFilter="blur(4px)"
      align="center"
      justify="center"
      p="1.5rem"
      onClick={onCancel}
    >
      <Box
        bg="paper.white"
        borderRadius="16px"
        maxW="640px"
        w="100%"
        maxH="85vh"
        overflowY="auto"
        p="1.8rem"
        boxShadow="0 24px 80px rgba(31,45,92,0.35)"
        onClick={(event) => event.stopPropagation()}
      >
        <Heading fontFamily="heading" fontSize="1.4rem" fontWeight="400" color="brand.ink" mb="1rem">
          ✨ Enhanced with AI
        </Heading>

        <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="ink.muted" mb="0.3rem">
          Original
        </Text>
        <Box bg="paper.warm" border="1px solid" borderColor="paper.linen" borderRadius="8px" p="0.8rem" mb="1.1rem">
          <Text fontSize="0.82rem" color="ink.soft" whiteSpace="pre-wrap">
            {original}
          </Text>
        </Box>

        <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.mid" mb="0.3rem">
          Enhanced — edit before applying
        </Text>
        <Textarea
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          minH="140px"
          resize="vertical"
          autoFocus
          {...inputStyles}
          borderColor="brand.pale"
        />

        <Flex justify="flex-end" gap="0.7rem" mt="1.3rem">
          <chakra.button
            type="button"
            onClick={onCancel}
            bg="paper.white"
            color="ink.body"
            border="1px solid"
            borderColor="paper.border"
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.12em"
            textTransform="uppercase"
            px="1.4rem"
            py="0.6rem"
            borderRadius="20px"
            cursor="pointer"
            _hover={{ borderColor: 'brand.mid', color: 'brand.mid' }}
          >
            Cancel
          </chakra.button>
          <chakra.button
            type="button"
            onClick={onApply}
            bg="brand.mid"
            color="paper.white"
            border="none"
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.12em"
            textTransform="uppercase"
            px="1.6rem"
            py="0.6rem"
            borderRadius="20px"
            cursor="pointer"
            _hover={{ bg: 'brand.ink' }}
          >
            Apply
          </chakra.button>
        </Flex>
      </Box>
    </Flex>
  );
}
